import type { SelectChangeEvent } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { createTicketResponse, getHelpTickets, updateHelpTicket } from '../../../services/api';
import type { HelpTicket, TicketStatus } from '../../../services/helpService';

const EMPTY_ARRAY: never[] = [];

export interface Filters {
  status: string;
  category: string;
  priority: string;
  assignedTo: string;
}

export interface Stats {
  total: number;
  open: number;
  inProgress: number;
  resolved: number;
  avgResponseTime: number;
}

export interface ExpandedRows {
  [ticketId: string]: boolean;
}

export const useHelpdeskData = () => {
  const { showSnackbar } = useSnackbar();

  const [tickets, setTickets] = useState<HelpTicket[]>([]);
  const [filteredTickets, setFilteredTickets] = useState<HelpTicket[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedTicket, setSelectedTicket] = useState<HelpTicket | null>(null);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [expandedRows, setExpandedRows] = useState<ExpandedRows>({});
  const [activeTab, setActiveTab] = useState<string>('all');

  const [filters, setFilters] = useState<Filters>({
    status: '',
    category: '',
    priority: '',
    assignedTo: '',
  });

  const [responseForm, setResponseForm] = useState<string>('');
  const [statusForm, setStatusForm] = useState<string>('');
  const [statusReason, setStatusReason] = useState<string>('');

  const [stats, setStats] = useState<Stats>({
    total: 0,
    open: 0,
    inProgress: 0,
    resolved: 0,
    avgResponseTime: 0,
  });

  const calculateStats = useCallback((ticketList: HelpTicket[]): void => {
    const total = ticketList.length;
    const open = ticketList.filter((t) => t.status === 'open').length;
    const inProgress = ticketList.filter((t) => t.status === 'in_progress').length;
    const resolved = ticketList.filter((t) => ['resolved', 'closed'].includes(t.status)).length;

    const respondedTickets = ticketList.filter((t) => t.timeToFirstResponse);
    const avgResponseTime =
      respondedTickets.length > 0
        ? Math.round(
            respondedTickets.reduce((sum, t) => sum + (t.timeToFirstResponse || 0), 0) /
              respondedTickets.length
          )
        : 0;

    setStats({ total, open, inProgress, resolved, avgResponseTime });
  }, []);

  const loadTickets = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await getHelpTickets({ limit: 100 });
      setTickets(response.tickets || EMPTY_ARRAY);
      calculateStats(response.tickets || EMPTY_ARRAY);
    } catch (error: unknown) {
      const err = error as Error;
      showSnackbar(`Failed to load tickets: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar, calculateStats]);

  const applyFilters = useCallback((): void => {
    let filtered: HelpTicket[] = [...tickets];

    if (activeTab !== 'all') {
      switch (activeTab) {
        case 'open':
          filtered = filtered.filter((t) => t.status === 'open');
          break;
        case 'active':
          filtered = filtered.filter((t) =>
            ['open', 'in_progress', 'waiting-response'].includes(t.status)
          );
          break;
        case 'resolved':
          filtered = filtered.filter((t) => ['resolved', 'closed'].includes(t.status));
          break;
        case 'urgent':
          filtered = filtered.filter((t) => t.priority === 'urgent');
          break;
        default:
          break;
      }
    }

    if (filters.status) {
      filtered = filtered.filter((t) => t.status === filters.status);
    }
    if (filters.category) {
      filtered = filtered.filter((t) => t.category === filters.category);
    }
    if (filters.priority) {
      filtered = filtered.filter((t) => t.priority === filters.priority);
    }
    if (filters.assignedTo) {
      filtered = filtered.filter((t) => t.assignedTo === filters.assignedTo);
    }

    setFilteredTickets(filtered);
  }, [tickets, filters, activeTab]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleTicketClick = async (ticket: HelpTicket): Promise<void> => {
    try {
      const response = await getHelpTickets({ ticketId: ticket.id });
      if (response.ticket) {
        setSelectedTicket(response.ticket);
        setResponseForm('');
        setStatusForm(response.ticket.status);
        setStatusReason('');
        setOpenDialog(true);
      }
    } catch (error: unknown) {
      const err = error as Error;
      showSnackbar(`Failed to load ticket details: ${err.message}`, 'error');
    }
  };

  const handleAddResponse = async (): Promise<void> => {
    if (!responseForm.trim()) {
      showSnackbar('Please enter a response message', 'error');
      return;
    }

    if (!selectedTicket) {
      return;
    }

    try {
      await createTicketResponse(selectedTicket.id, responseForm.trim());
      showSnackbar('Response added successfully!', 'success');

      const response = await getHelpTickets({ ticketId: selectedTicket.id });
      setSelectedTicket(response.ticket || null);
      setResponseForm('');

      loadTickets();
    } catch (error: unknown) {
      const err = error as Error;
      showSnackbar(err.message, 'error');
    }
  };

  const handleUpdateStatus = async (): Promise<void> => {
    if (!statusForm) {
      showSnackbar('Please select a status', 'error');
      return;
    }

    if (!selectedTicket) {
      return;
    }

    try {
      const payload: { updateType: 'status'; status: TicketStatus; reason?: string } = {
        updateType: 'status',
        status: statusForm as TicketStatus,
      };

      if (statusReason) {
        payload.reason = statusReason;
      }

      await updateHelpTicket(selectedTicket.id, payload);
      showSnackbar('Status updated successfully!', 'success');

      const response = await getHelpTickets({ ticketId: selectedTicket.id });
      setSelectedTicket(response.ticket || null);
      setStatusReason('');

      loadTickets();
    } catch (error: unknown) {
      const err = error as Error;
      showSnackbar(`Failed to update status: ${err.message}`, 'error');
    }
  };

  const toggleRowExpansion = (ticketId: string): void => {
    setExpandedRows((prev) => ({
      ...prev,
      [ticketId]: !prev[ticketId],
    }));
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: string): void => {
    setActiveTab(newValue);
  };

  const handleFilterChange =
    (field: keyof Filters) =>
    (event: SelectChangeEvent<string>): void => {
      setFilters({ ...filters, [field]: event.target.value });
    };

  const clearFilters = (): void => {
    setFilters({ status: '', category: '', priority: '', assignedTo: '' });
  };

  const handleResponseFormChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setResponseForm(event.target.value);
  };

  const handleStatusFormChange = (event: SelectChangeEvent<string>): void => {
    setStatusForm(event.target.value);
  };

  const handleStatusReasonChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setStatusReason(event.target.value);
  };

  const handleQuickResolve = (): void => {
    setStatusForm('resolved');
    setStatusReason('Issue resolved by support');
    // Using a timeout to ensure state settles before update, or just direct pass parameters.
    // To be clean, handleUpdateStatus is modified below.
  };

  const handleQuickClose = (): void => {
    setStatusForm('closed');
    setStatusReason('Ticket closed');
  };

  return {
    loading,
    stats,
    activeTab,
    filters,
    filteredTickets,
    expandedRows,
    openDialog,
    selectedTicket,
    responseForm,
    statusForm,
    statusReason,

    setOpenDialog,
    loadTickets,
    handleTicketClick,
    toggleRowExpansion,
    handleTabChange,
    handleFilterChange,
    clearFilters,
    handleResponseFormChange,
    handleStatusFormChange,
    handleStatusReasonChange,
    handleAddResponse,
    handleUpdateStatus,
    handleQuickResolve,
    handleQuickClose,
  };
};
